import React, { useEffect, useState } from 'react';
import {
  Typography,
  FlexGrid,
  FlexGridRow,
  FlexGridCol,
  StackView,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
} from '@telus-uds/components-web';
import { CopyBold, Checkmark } from '@telus-uds/palette-allium/build/web/icons';
import useAuth from '../../hooks/useAuth';
import { BACKEND_BASE_URL } from '../../constants/api';

export const EXTERNAL_APIS_URL = `${BACKEND_BASE_URL}/api/external-apis`;

/** Lists every backend API's base URL, with a ready-made authenticated curl for each. */
export const ApiDiscoveryPage = () => {
  const { jwt, isLoading: authLoading } = useAuth();

  const [registry, setRegistry] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copiedPluginId, setCopiedPluginId] = useState('');

  useEffect(() => {
    if (authLoading || !jwt) return;
    (async () => {
      try {
        const res = await fetch(EXTERNAL_APIS_URL, { headers: { Authorization: `Bearer ${jwt}` } });
        if (!res.ok) throw new Error(await res.text());
        setRegistry(await res.json());
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load API registry');
      } finally {
        setLoading(false);
      }
    })();
  }, [authLoading, jwt]);

  if (loading) {
    return (
      <div style={{ padding: '24px' }}>
        <Typography block>Loading...</Typography>
      </div>
    );
  }

  const entries = Object.entries(registry).sort(([a], [b]) => a.localeCompare(b));

  const handleCopy = (pluginId: string, url: string) => {
    void navigator.clipboard.writeText(`curl -H "Authorization: Bearer ${jwt}" ${url}/health`);
    setCopiedPluginId(pluginId);
    setTimeout(() => setCopiedPluginId(''), 2000);
  };

  return (
    <div style={{ padding: '24px' }}>
      <FlexGrid>
        <FlexGridRow>
          <FlexGridCol xs={12}>
            <div style={{ marginBottom: '24px' }}>
              <Typography variant={{ size: 'h1' }}>Simplify API Registry</Typography>
            </div>
          </FlexGridCol>
        </FlexGridRow>
        <FlexGridRow>
          <FlexGridCol xs={12}>
            <StackView space={{ md: 1 }}>
              {error && <Typography block>{error}</Typography>}
              <Table>
                <TableHeader>
                  <TableCell>Plugin ID</TableCell>
                  <TableCell>URL</TableCell>
                  <TableCell> </TableCell>
                </TableHeader>
                <TableBody>
                  {entries.map(([pluginId, url]) => (
                    <TableRow key={pluginId}>
                      <TableCell>{pluginId}</TableCell>
                      <TableCell>
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          // Browser-default link blue is unreadable on the dark surface.
                          style={{ color: 'var(--color-info, #0969da)' }}
                        >
                          {url}
                        </a>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          icon={(copiedPluginId === pluginId ? Checkmark : CopyBold) as React.ComponentType}
                          tokens={{ borderColor: 'transparent', borderWidth: 0, outerBorderWidth: 0 }}
                          aria-label={`Copy curl command for ${pluginId}`}
                          onClick={() => handleCopy(pluginId, url)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </StackView>
          </FlexGridCol>
        </FlexGridRow>
      </FlexGrid>
    </div>
  );
};

export default ApiDiscoveryPage;
